import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Phone, Mail, Clock, Send, Facebook, Twitter, Linkedin, Youtube, MessageSquare, Calendar, Globe } from 'lucide-react';
interface ContactProps {
  onBack?: () => void;
}
const contactInfo = [{
  icon: MapPin,
  title: 'Head Office',
  titleAr: 'المكتب الرئيسي',
  details: ['PO BOX 406, PC100', 'Bawshar, Muscat', 'Sultanate of Oman'],
  color: '#006847'
}, {
  icon: Phone,
  title: 'Phone',
  titleAr: 'الهاتف',
  details: ['+968 7760 4646', '+968 7672 7225', 'WhatsApp: +968 9444 8686'],
  color: '#C8102E'
}, {
  icon: Mail,
  title: 'Email',
  titleAr: 'البريد الإلكتروني',
  details: ['info@bareeqtech.net', 'support@bareeqtech.net', 'sales@bareeqtech.net'],
  color: '#D4AF37'
}, {
  icon: Clock,
  title: 'Business Hours',
  titleAr: 'ساعات العمل',
  details: ['Sunday - Thursday', '8:00 AM - 5:00 PM', 'Friday & Saturday: Closed'],
  color: '#3498DB'
}];
const departments = [{
  value: 'general',
  label: 'General Inquiry'
}, {
  value: 'sales',
  label: 'Sales & Consulting'
}, {
  value: 'support',
  label: 'Technical Support'
}, {
  value: 'careers',
  label: 'Careers'
}, {
  value: 'partnership',
  label: 'Partnership Opportunities'
}];
const offices = [{
  city: 'Muscat',
  cityAr: 'مسقط',
  address: 'PO BOX 406, PC100, Bawshar, Muscat, Sultanate of Oman',
  phone: '+968 7760 4646 | +968 7672 7225',
  isHQ: true
}];
export default function Contact({
  onBack
}: ContactProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    department: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setErrorMsg('');
    try {
      const res = await fetch('/wp-json/bareeq/v1/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'contact-page' })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', company: '', department: '', subject: '', message: '' });
        setTimeout(() => setSubmitted(false), 6000);
      } else {
        setErrorMsg(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setErrorMsg('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };
  return <div className="min-h-screen bg-gray-50">
      {/* Header with Back Button */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-[#006847] transition-colors">
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Home</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-[#006847] to-[#004d35] text-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Get in Touch
            </h1>
            <p className="text-xl text-white/90 mb-6">
              تواصل معنا
            </p>
            <p className="text-lg text-white/80">
              Have questions about our services? Our team is ready to help you with 
              your IT and cybersecurity needs.
            </p>
          </motion.div>
        </div>

        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 48h1440V0c-240 48-480 48-720 24C480 0 240 0 0 24v24z" fill="#F9FAFB" />
          </svg>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{
              backgroundColor: `${info.color}20`
            }}>
                  <info.icon style={{
                color: info.color
              }} size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-1">
                  {info.title}
                </h3>
                <p className="text-sm text-gray-500 mb-4">{info.titleAr}</p>
                <div className="space-y-2">
                  {info.details.map((detail, i) => <p key={i} className="text-gray-600">
                      {detail}
                    </p>)}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }}>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Send Us a Message
              </h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and we'll get back to you within 24 hours
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input type="text" placeholder="Full Name *" required value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#006847] focus:outline-none" />
                  <input type="email" placeholder="Email Address *" required value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#006847] focus:outline-none" />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <input type="tel" placeholder="Phone Number *" required value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#006847] focus:outline-none" />
                  <input type="text" placeholder="Company Name" value={formData.company} onChange={e => setFormData({
                  ...formData,
                  company: e.target.value
                })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#006847] focus:outline-none" />
                </div>

                <select value={formData.department} onChange={e => setFormData({
                ...formData,
                department: e.target.value
              })} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#006847] focus:outline-none">
                  <option value="">Select Department *</option>
                  {departments.map(dept => <option key={dept.value} value={dept.value}>
                      {dept.label}
                    </option>)}
                </select>

                <input type="text" placeholder="Subject *" required value={formData.subject} onChange={e => setFormData({
                ...formData,
                subject: e.target.value
              })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#006847] focus:outline-none" />

                <textarea placeholder="Your Message *" required rows={6} value={formData.message} onChange={e => setFormData({
                ...formData,
                message: e.target.value
              })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#006847] focus:outline-none resize-none" />

                <button type="submit" disabled={sending} className="w-full bg-[#006847] hover:bg-[#005438] disabled:opacity-60 text-white px-8 py-4 rounded-full font-semibold transition-colors flex items-center justify-center gap-2">
                  <Send size={20} />
                  {sending ? 'Sending...' : 'Send Message'}
                </button>

                {errorMsg && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{errorMsg}</div>}

                {submitted && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                    Thank you! Your message has been sent successfully.
                  </motion.div>}
              </form>
            </motion.div>

            {/* Map & Quick Actions */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="space-y-6">
              <div className="bg-gray-200 rounded-2xl overflow-hidden h-80">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3656.1825415871575!2d58.40595931544425!3d23.59142098467911!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDM1JzI5LjEiTiA1OMKwMjQnMjguOCJF!5e0!3m2!1sen!2som!4v1234567890123!5m2!1sen!2som" width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>

              <div className="bg-gradient-to-r from-[#006847] to-[#004d35] text-white p-8 rounded-2xl">
                <h3 className="text-2xl font-bold mb-6">Quick Actions</h3>
                <div className="space-y-4">
                  <button className="w-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 px-6 py-4 rounded-lg transition-colors flex items-center justify-between" onClick={onBack}>
                    <div className="flex items-center gap-3">
                      <Calendar size={24} />
                      <span className="font-medium">Schedule Consultation</span>
                    </div>
                    <ArrowLeft className="rotate-180" size={20} />
                  </button>

                  <button className="w-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 px-6 py-4 rounded-lg transition-colors flex items-center justify-between" onClick={() => alert('Live chat coming soon! Please use the contact form or call us.')}>
                    <div className="flex items-center gap-3">
                      <MessageSquare size={24} />
                      <span className="font-medium">Live Chat Support</span>
                    </div>
                    <ArrowLeft className="rotate-180" size={20} />
                  </button>

                  <a href="tel:+96877604646" className="w-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 px-6 py-4 rounded-lg transition-colors flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Phone size={24} />
                      <span className="font-medium">Call Now</span>
                    </div>
                    <ArrowLeft className="rotate-180" size={20} />
                  </a>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <h4 className="font-semibold text-gray-800 mb-4">Follow Us</h4>
                <div className="flex gap-3">
                  {[Facebook, Twitter, Linkedin, Youtube].map((Icon, i) => <a key={i} href="#" className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#006847] hover:text-white transition-colors">
                      <Icon size={20} />
                    </a>)}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Our Offices
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Visit us at any of our regional offices
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {offices.map((office, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-lg transition-shadow relative">
                {office.isHQ && <div className="absolute top-4 right-4 bg-[#D4AF37] text-white text-xs px-3 py-1 rounded-full font-semibold">
                    HQ
                  </div>}
                <Globe className="text-[#006847] mb-4" size={32} />
                <h3 className="text-xl font-bold text-gray-800 mb-1">
                  {office.city}
                </h3>
                <p className="text-sm text-gray-500 mb-4">{office.cityAr}</p>
                <p className="text-gray-600 mb-2">{office.address}</p>
                <p className="text-[#006847] font-medium">{office.phone}</p>
              </motion.div>)}
          </div>
        </div>
      </section>
    </div>;
}